import React from 'react';
import { ChartConfig, ScientificChartConfig } from '../types';
import { Settings, Grid, Activity, Layers, Move, Maximize } from 'lucide-react';

interface ScientificControlsProps {
    config: ChartConfig;
    onChange: (newConfig: ScientificChartConfig) => void;
    onConfigChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    allCols: string[];
    numericCols: string[];
}

export const ScientificControls: React.FC<ScientificControlsProps> = ({ config, onChange, onConfigChange, allCols, numericCols }) => {
    const defaults: ScientificChartConfig = {
        plotType: 'line',
        subplots: { rows: 1, cols: 1 },
        fieldMappings: {},
        axisOptions: {
            logScaleX: false,
            logScaleY: false,
            grid: true,
            minorTicks: false,
            minorGrid: false,
            axisFormat: 'plain'
        },
        analysis: {
            trendline: 'none',
            showEquation: false,
            slopeMode: 'overall',
            slopeRange: { min: 0, max: 100 }
        },
        errorBars: {
            show: false,
            mode: 'none',
            constantValue: 1,
            direction: 'symmetric',
            capSize: 5,
            lineThickness: 1
        },
        transformations: {
            x: 'none',
            y: 'none',
            normalize: false,
            standardize: false
        },
        style: {
            colormap: 'viridis',
            showColorbar: true,
            markerShape: 'circle',
            markerSize: 6,
            markerSizeMin: 2,
            markerSizeMax: 20,
            markerOutline: false,
            lineStyle: 'solid',
            lineWidth: 2,
            curveSmoothing: false,
            alpha: 0.8,
            showPointMarkers: true
        },
        legend: {
            show: true,
            position: 'right'
        },
        animation: {
            fadeIn: true,
            duration: 800
        },
        heatmap: {
            colorScale: 'sequential',
            resolution: 50,
            showContours: false,
            contourCount: 10,
            interpolation: true
        },
        threeD: {
            elevation: 30,
            azimuth: 45,
            projection: 'perspective',
            colorGradient: 'viridis',
            pointSize: 4,
            depthShading: false,
            showAxisBox: true,
            wireframeMode: false,
            meshDensity: 10
        },
        vector: {
            scale: 1,
            arrowSize: 1,
            normalize: false,
            autoGrid: false,
            magnitudeColoring: false
        }
    };

    const scientificConfig = { ...defaults, ...(config.advanced?.scientific || config.scientificConfig || {}) };

    const handleChange = (key: keyof ScientificChartConfig, value: any) => {
        onChange({ ...scientificConfig, [key]: value });
    };

    const handleNestedChange = (parent: keyof ScientificChartConfig, key: string, value: any) => {
        onChange({
            ...scientificConfig,
            [parent]: { ...scientificConfig[parent] as any, [key]: value }
        });
    };

    const { plotType } = scientificConfig;
    const showAnalysis = ['scatter', 'line'].includes(plotType);
    const showErrorBars = ['errorbar'].includes(plotType);
    const showMarkers = ['scatter', 'line', 'errorbar', '3d-scatter'].includes(plotType);

    return (
        <div className="space-y-6 p-4 bg-obsidian-900/50 rounded-xl border border-gold-500/10">
            {/* Data Mapping moved to Left Sidebar */}

            {/* Plot Type, Analysis, Error Bars, Transformations, Axis Control moved to Left Sidebar */}

            {/* Axis Options */}
            <div className="space-y-3 border-t border-gold-500/10 pt-4">
                <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                    <Maximize className="w-3.5 h-3.5" /> Axis Scaling
                </h4>
                <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                            type="checkbox"
                            checked={scientificConfig.axisOptions?.logScaleX || false}
                            onChange={(e) => handleNestedChange('axisOptions', 'logScaleX', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                        />
                        Log Scale X
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                            type="checkbox"
                            checked={scientificConfig.axisOptions?.logScaleY || false}
                            onChange={(e) => handleNestedChange('axisOptions', 'logScaleY', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                        />
                        Log Scale Y
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                            type="checkbox"
                            checked={scientificConfig.axisOptions?.minorGrid || false}
                            onChange={(e) => handleNestedChange('axisOptions', 'minorGrid', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                        />
                        Minor Grid
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                            type="checkbox"
                            checked={scientificConfig.axisOptions?.minorTicks || false}
                            onChange={(e) => handleNestedChange('axisOptions', 'minorTicks', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                        />
                        Minor Ticks
                    </label>

                    <div className="col-span-2 space-y-1 mt-2 border-t border-gold-500/10 pt-2">
                        <label className="text-xs font-semibold text-gray-400">Axis Label Format</label>
                        <select
                            value={scientificConfig.axisOptions?.axisFormat || 'plain'}
                            onChange={(e) => handleNestedChange('axisOptions', 'axisFormat', e.target.value)}
                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        >
                            <option value="plain">Plain</option>
                            <option value="scientific">Scientific Notation</option>
                            <option value="engineering">Engineering</option>
                        </select>
                    </div>

                    {scientificConfig.plotType.startsWith('3d') && (
                        <label className="flex items-center gap-2 text-sm text-gray-300 col-span-2 border-t border-gold-500/10 pt-2">
                            <input
                                type="checkbox"
                                checked={scientificConfig.axisOptions?.logScaleZ || false}
                                onChange={(e) => handleNestedChange('axisOptions', 'logScaleZ', e.target.checked)}
                                className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                            />
                            Log Scale Z
                        </label>
                    )}
                </div>
            </div>

            {/* Analysis (Trendlines) - Only for Scatter/Line */}
            {showAnalysis && (
                <div className="space-y-3 border-t border-gold-500/10 pt-4">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5" /> Analysis
                    </h4>
                    <div className="space-y-2">
                        <div>
                            <label className="text-xs text-gray-400 block mb-1">Trendline</label>
                            <select
                                value={scientificConfig.analysis?.trendline || 'none'}
                                onChange={(e) => handleNestedChange('analysis', 'trendline', e.target.value)}
                                className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            >
                                <option value="none">None</option>
                                <option value="linear">Linear Regression</option>
                                <option value="poly2">Polynomial (2nd Degree)</option>
                                <option value="exp">Exponential</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Bars - Only for ErrorBar Plot */}
            {showErrorBars && (
                <div className="space-y-3 border-t border-gold-500/10 pt-4">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Settings className="w-3.5 h-3.5" /> Error Bars
                    </h4>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm text-gray-300 font-bold">
                            <input
                                type="checkbox"
                                checked={scientificConfig.errorBars?.show || false}
                                onChange={(e) => handleNestedChange('errorBars', 'show', e.target.checked)}
                                className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                            />
                            Enable Error Bars
                        </label>

                        {scientificConfig.errorBars?.show && (
                            <div className="pl-6 space-y-2 border-l-2 border-gold-500/10 ml-1">
                                <div>
                                    <label className="text-xs text-gray-400 block mb-1">Error Mode</label>
                                    <select
                                        value={scientificConfig.errorBars.mode}
                                        onChange={(e) => handleNestedChange('errorBars', 'mode', e.target.value)}
                                        className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                    >
                                        <option value="none">None</option>
                                        <option value="constant">Constant Value</option>
                                        <option value="field">From Fields (Symmetric)</option>
                                        <option value="bounds">From Bounds (Asymmetric)</option>
                                    </select>
                                </div>
                                {scientificConfig.errorBars.mode === 'constant' && (
                                    <div>
                                        <label className="text-xs text-gray-400 block mb-1">Constant Error Value</label>
                                        <input
                                            type="number"
                                            value={scientificConfig.errorBars.constantValue}
                                            onChange={(e) => handleNestedChange('errorBars', 'constantValue', parseFloat(e.target.value))}
                                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="text-xs text-gray-400 block mb-1">Cap Size</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="20"
                                        value={scientificConfig.errorBars.capSize}
                                        onChange={(e) => handleNestedChange('errorBars', 'capSize', parseInt(e.target.value))}
                                        className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Styles & Appearance - For Plots with Markers */}
            {showMarkers && (
                <div className="space-y-3 border-t border-gold-500/10 pt-4">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Settings className="w-3.5 h-3.5" /> Appearance
                    </h4>
                    <div className="space-y-2">
                        <div className="space-y-1 pt-2 border-t border-gold-500/10">
                            <label className="text-xs font-semibold text-gray-400">Marker Style</label>
                            <div className="grid grid-cols-2 gap-2">
                                <select
                                    value={scientificConfig.style?.markerShape || 'circle'}
                                    onChange={(e) => handleNestedChange('style', 'markerShape', e.target.value)}
                                    className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                >
                                    <option value="circle">Circle</option>
                                    <option value="square">Square</option>
                                    <option value="triangle">Triangle</option>
                                    <option value="cross">Cross</option>
                                    <option value="star">Star</option>
                                    <option value="diamond">Diamond</option>
                                </select>
                                <input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={scientificConfig.style?.markerSize || 4}
                                    onChange={(e) => handleNestedChange('style', 'markerSize', parseInt(e.target.value))}
                                    className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                                    placeholder="Size"
                                />
                            </div>
                            <div className="flex items-center gap-4 mt-2">
                                <label className="flex items-center gap-2 text-sm text-gray-300">
                                    <input
                                        type="checkbox"
                                        checked={scientificConfig.style?.markerOutline || false}
                                        onChange={(e) => handleNestedChange('style', 'markerOutline', e.target.checked)}
                                        className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                                    />
                                    Outline
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Subplot Grid */}
            <div className="space-y-2 border-t border-gold-500/10 pt-4">
                <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                    <Grid className="w-3.5 h-3.5" /> Subplots Grid
                </h4>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Rows</label>
                        <input
                            type="number"
                            min="1"
                            max="5"
                            value={scientificConfig.subplots.rows}
                            onChange={(e) => handleNestedChange('subplots', 'rows', parseInt(e.target.value))}
                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Cols</label>
                        <input
                            type="number"
                            min="1"
                            max="5"
                            value={scientificConfig.subplots.cols}
                            onChange={(e) => handleNestedChange('subplots', 'cols', parseInt(e.target.value))}
                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                        />
                    </div>
                </div>
            </div>

            {/* 3D Controls (Conditional) */}
            {scientificConfig.plotType.startsWith('3d-') && (
                <div className="space-y-2 border-t border-gold-500/10 pt-4">
                    <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                        <Move className="w-3.5 h-3.5" /> 3D View
                    </h4>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Azimuth: {scientificConfig.threeD.azimuth}°</label>
                        <input
                            type="range"
                            min="0"
                            max="360"
                            value={scientificConfig.threeD.azimuth}
                            onChange={(e) => handleNestedChange('threeD', 'azimuth', parseInt(e.target.value))}
                            className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Elevation: {scientificConfig.threeD.elevation}°</label>
                        <input
                            type="range"
                            min="-90"
                            max="90"
                            value={scientificConfig.threeD.elevation}
                            onChange={(e) => handleNestedChange('threeD', 'elevation', parseInt(e.target.value))}
                            className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
